import { INBOUND_MSG_TYPE as SIMT } from "../server-contracts/inboundMsgTypes.js"; // SIMT = Server Inbound Msg Types
import { OUTBOUND_MSG_TYPE as SOMT } from "../server-contracts/outboundMsgTypes.js"; // SOMT = Server Outbound Msg Types
import { PROTOCOL_EVENT_TYPE as PET } from "./protocolEventTypes.js";
function assertCallId(frame) {
    if (!frame.callId) {
        throw new Error(`Expected callId for frame type ${frame.type}`);
    }
    return frame.callId;
}
/**
 * mapServerToClientFrameToEvent
 * -----------------------------
 *
 * Converts a raw server->client frame into a normalized protocol event
 * from the client's point of view.
 *
 * This function is pure.
 */
export function mapServerToClientFrameToEvent(frame) {
    switch (frame.type) {
        case SOMT.WELCOME:
            return {
                type: PET.WELCOME_RECEIVED,
                frame,
                payload: frame.payload,
            };
        case SOMT.ERROR:
            return {
                type: PET.ERROR_RECEIVED,
                frame,
                payload: frame.payload,
            };
        case SOMT.PONG:
            return {
                type: PET.PONG_RECEIVED,
                frame,
                payload: frame.payload,
            };
        case SOMT.CALL_CREATED:
            return {
                type: PET.CALL_CREATED_RECEIVED,
                frame,
                callId: assertCallId(frame),
                payload: frame.payload,
            };
        case SOMT.CALL_INCOMING:
            return {
                type: PET.CALL_INCOMING_RECEIVED,
                frame,
                callId: assertCallId(frame),
                payload: frame.payload,
            };
        case SOMT.CALL_ACCEPTED:
            return {
                type: PET.CALL_ACCEPTED_RECEIVED,
                frame,
                callId: assertCallId(frame),
                payload: frame.payload,
            };
        case SOMT.CALL_RECONNECTING:
            return {
                type: PET.CALL_RECONNECTING_RECEIVED,
                frame,
                callId: assertCallId(frame),
                payload: frame.payload,
            };
        case SOMT.CALL_IN_CALL:
            return {
                type: PET.CALL_IN_CALL_RECEIVED,
                frame,
                callId: assertCallId(frame),
                payload: frame.payload,
            };
        case SOMT.CALL_RESUMED:
            return {
                type: PET.CALL_RESUMED_RECEIVED,
                frame,
                callId: assertCallId(frame),
                payload: frame.payload,
            };
        case SOMT.CALL_ENDED:
            return {
                type: PET.CALL_ENDED_RECEIVED,
                frame,
                callId: assertCallId(frame),
                payload: frame.payload,
            };
        case SOMT.SDP_OFFER:
            return {
                type: PET.SDP_OFFER_RECEIVED,
                frame,
                callId: assertCallId(frame),
                payload: frame.payload,
            };
        case SOMT.SDP_ANSWER:
            return {
                type: PET.SDP_ANSWER_RECEIVED,
                frame,
                callId: assertCallId(frame),
                payload: frame.payload,
            };
        case SOMT.ICE_CANDIDATE:
            return {
                type: PET.ICE_CANDIDATE_RECEIVED,
                frame,
                callId: assertCallId(frame),
                payload: frame.payload,
            };
        case SOMT.CALL_STATE:
            return {
                type: PET.CALL_STATE_RECEIVED,
                frame,
                callId: assertCallId(frame),
                payload: frame.payload,
            };
        default: {
            const _exhaustive = frame;
            return _exhaustive;
        }
    }
}
export function makeHelloFrame(input) {
    return {
        v: input.v,
        type: SIMT.HELLO,
        msgId: input.msgId,
        ts: input.ts,
        seq: input.seq,
        ack: input.ack,
        payload: {
            token: input.token,
            deviceId: input.deviceId,
            ...(input.role !== undefined ? { role: input.role } : {}),
            ...(input.resume !== undefined ? { resume: input.resume } : {}),
            ...(input.clientInfo !== undefined ? { clientInfo: input.clientInfo } : {}),
        },
    };
}
export function makePingFrame(input) {
    return {
        v: input.v,
        type: SIMT.PING,
        msgId: input.msgId,
        ts: input.ts,
        seq: input.seq,
        ack: input.ack,
        payload: {},
    };
}
export function makeAckFrame(input) {
    return {
        v: input.v,
        type: SIMT.ACK,
        msgId: input.msgId,
        ts: input.ts,
        seq: input.seq,
        ack: input.ack,
        payload: {},
    };
}
export function makeCallCreateFrame(input) {
    return {
        v: input.v,
        type: SIMT.CALL_CREATE,
        msgId: input.msgId,
        ts: input.ts,
        seq: input.seq,
        ack: input.ack,
        payload: {
            calleeUserId: input.calleeUserId,
            ...(input.media !== undefined ? { media: input.media } : {}),
            ...(input.features !== undefined ? { features: input.features } : {}),
        },
    };
}
export function makeCallAcceptFrame(input) {
    return {
        v: input.v,
        type: SIMT.CALL_ACCEPT,
        msgId: input.msgId,
        ts: input.ts,
        seq: input.seq,
        ack: input.ack,
        callId: input.callId,
        payload: {},
    };
}
export function makeCallRejectFrame(input) {
    return {
        v: input.v,
        type: SIMT.CALL_REJECT,
        msgId: input.msgId,
        ts: input.ts,
        seq: input.seq,
        ack: input.ack,
        callId: input.callId,
        payload: {},
    };
}
export function makeCallEndFrame(input) {
    return {
        v: input.v,
        type: SIMT.CALL_END,
        msgId: input.msgId,
        ts: input.ts,
        seq: input.seq,
        ack: input.ack,
        callId: input.callId,
        payload: {
            ...(input.reason !== undefined ? { reason: input.reason } : {}),
        },
    };
}
export function makeSdpOfferFrame(input) {
    return {
        v: input.v,
        type: SIMT.SDP_OFFER,
        msgId: input.msgId,
        ts: input.ts,
        seq: input.seq,
        ack: input.ack,
        callId: input.callId,
        payload: {
            sdp: input.sdp,
            sdpType: "offer",
        },
    };
}
export function makeSdpAnswerFrame(input) {
    return {
        v: input.v,
        type: SIMT.SDP_ANSWER,
        msgId: input.msgId,
        ts: input.ts,
        seq: input.seq,
        ack: input.ack,
        callId: input.callId,
        payload: {
            sdp: input.sdp,
            sdpType: "answer",
        },
    };
}
export function makeIceCandidateFrame(input) {
    return {
        v: input.v,
        type: SIMT.ICE_CANDIDATE,
        msgId: input.msgId,
        ts: input.ts,
        seq: input.seq,
        ack: input.ack,
        callId: input.callId,
        payload: {
            candidate: input.candidate,
        },
    };
}
export function makePcConnectionStateFrame(input) {
    return {
        v: input.v,
        type: SIMT.PC_CONNECTION_STATE,
        msgId: input.msgId,
        ts: input.ts,
        seq: input.seq,
        ack: input.ack,
        callId: input.callId,
        payload: {
            connectionState: input.connectionState,
        },
    };
}
